import React, { useState, useEffect } from 'react';
import { Trophy, Award, Calendar, MapPin, Star } from 'lucide-react';

const WinnersGallery = () => {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const BACKEND_URL = window.location.origin;

  useEffect(() => {
    fetchWinners();
  }, []);

  const fetchWinners = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/winners/gallery?limit=50`);
      const data = await response.json();
      setWinners(data.winners || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching winners:', error);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-gray-900 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-white text-xl">Loading winners...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-gray-900 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Trophy className="w-16 h-16 text-yellow-400 animate-bounce" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-4">
            🏆 HALL OF CHAMPIONS 🏆
          </h1>
          <p className="text-xl text-gray-300">
            Celebrating Our Fokatmain Winners
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-yellow-500/20 px-6 py-3 rounded-full border border-yellow-500/50">
            <Star className="w-5 h-5 text-yellow-400" />
            <span className="text-yellow-300 font-semibold">{winners.length} Winners & Counting!</span>
          </div>
        </div>

        {/* Winners Count */}
        {winners.length === 0 ? (
          <div className="text-center py-20">
            <Trophy className="w-20 h-20 mx-auto mb-4 text-gray-600" />
            <h3 className="text-2xl font-bold text-white mb-2">No Winners Yet</h3>
            <p className="text-gray-400">Be the first to win amazing prizes!</p>
          </div>
        ) : (
          <>
            {/* Winners Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
              {winners.map((winner, index) => (
                <div 
                  key={winner.winner_id}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden border border-yellow-500/30 hover:border-yellow-500/60 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-yellow-500/20"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Photo */}
                  <div className="relative aspect-square bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                    {winner.winner_photo_url ? (
                      <img 
                        src={winner.winner_photo_url} 
                        alt={winner.player_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-8xl">🏆</div>
                    )}
                    {/* Winner Badge */}
                    <div className="absolute top-2 right-2 bg-yellow-500 text-black font-bold px-3 py-1 rounded-full text-xs flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      WINNER
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    {/* Name */}
                    <h3 className="text-xl font-bold text-white mb-1 truncate">
                      {winner.player_name}
                    </h3>

                    {/* City */}
                    <div className="flex items-center gap-1 text-gray-400 text-sm mb-3">
                      <MapPin className="w-3 h-3" />
                      <span>{winner.player_city}, {winner.player_state}</span>
                    </div>

                    {/* Prize */}
                    <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg p-3 mb-3 border border-yellow-500/30">
                      <div className="text-yellow-400 font-semibold text-sm mb-1">Won</div>
                      <div className="text-white font-bold">{winner.prize_name}</div>
                    </div>

                    {/* Game */}
                    <div className="text-gray-400 text-sm mb-2">
                      <span className="font-semibold">Game:</span> {winner.game_name}
                    </div>

                    {/* Score */}
                    <div className="text-gray-400 text-sm mb-3">
                      <span className="font-semibold">Score:</span> <span className="text-green-400 font-bold">{winner.score}</span>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-1 text-gray-500 text-xs">
                      <Calendar className="w-3 h-3" />
                      <span>{formatDate(winner.competition_date)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Call to Action */}
        <div className="text-center mt-16 bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-yellow-500/10 border border-yellow-500/30 rounded-2xl p-8">
          <Trophy className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
          <h3 className="text-2xl font-bold text-white mb-3">
            Want to See Your Name Here?
          </h3>
          <p className="text-gray-300 mb-6">
            Join our next competition and win amazing prizes!
          </p>
          <button
            onClick={() => window.location.href = '/play-games'}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold px-8 py-4 rounded-full hover:from-yellow-600 hover:to-orange-600 transition-all transform hover:scale-105 shadow-lg"
          >
            Play Now & Win! 🎮
          </button>
        </div>
      </div>
    </div>
  );
};

export default WinnersGallery;
